if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.button = this.querySelector('.sticky-add-to-cart__button');
        this.buttonText = this.button.querySelector('span');
        this.spinner = this.button.querySelector('.loading__spinner');
        this.priceWrapper = this.querySelector('.sticky-add-to-cart__price');

        this.realSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.realPrice = document.getElementById(`price-${this.sectionId}`);
        this.variantSelects = this.dataset.variantSelectsId
          ? document.getElementById(this.dataset.variantSelectsId)
          : null;

        this.button.addEventListener('click', this.onClick.bind(this));
      }

      connectedCallback() {
        if (!this.realSubmitButton) return;

        this.syncButtonState();

        this.buttonObserver = new MutationObserver(this.syncButtonState.bind(this));
        this.buttonObserver.observe(this.realSubmitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        if (this.realPrice) {
          this.syncPrice();
          this.priceObserver = new MutationObserver(this.syncPrice.bind(this));
          this.priceObserver.observe(this.realPrice, { childList: true, subtree: true, characterData: true });
        }

        this.scrollObserver = new IntersectionObserver(this.handleIntersection.bind(this));
        this.scrollObserver.observe(this.realSubmitButton);

        // Keep a CSS var in sync with our own rendered height so other fixed-position
        // elements (e.g. a "back to top" button from a separate app) can be pushed
        // above us via CSS while we're visible, instead of being covered by us.
        this.sizeObserver = new ResizeObserver(([entry]) => {
          document.documentElement.style.setProperty('--sticky-atc-height', `${entry.borderBoxSize?.[0]?.blockSize ?? this.offsetHeight}px`);
        });
        this.sizeObserver.observe(this);
      }

      disconnectedCallback() {
        this.buttonObserver?.disconnect();
        this.priceObserver?.disconnect();
        this.scrollObserver?.disconnect();
        this.sizeObserver?.disconnect();
        document.body.classList.remove('sticky-add-to-cart-is-visible');
      }

      handleIntersection(entries) {
        const entry = entries[entries.length - 1];
        const isScrolledPast = entry.boundingClientRect.bottom < 0;
        this.classList.toggle('sticky-add-to-cart--visible', isScrolledPast);
        document.body.classList.toggle('sticky-add-to-cart-is-visible', isScrolledPast);
      }

      syncButtonState() {
        // Note: the sticky button is intentionally never given a native `disabled`
        // attribute. When the real button is hard-disabled (unavailable/sold out for
        // the selected variant), tapping the sticky button should still work so it can
        // redirect the shopper to the variant picker instead of doing nothing.
        const isUnavailable = this.realSubmitButton.hasAttribute('disabled');
        const isLoading = this.realSubmitButton.classList.contains('loading');

        this.button.setAttribute('aria-disabled', isUnavailable || isLoading);
        this.button.classList.toggle('loading', isLoading);

        const realText = this.realSubmitButton.querySelector('span')?.textContent;
        if (realText && this.buttonText) this.buttonText.textContent = realText;

        const realSpinnerVisible = !this.realSubmitButton
          .querySelector('.loading__spinner')
          ?.classList.contains('hidden');
        this.spinner?.classList.toggle('hidden', !realSpinnerVisible);
      }

      syncPrice() {
        if (!this.realPrice || !this.priceWrapper) return;
        this.priceWrapper.innerHTML = this.realPrice.innerHTML;
      }

      onClick() {
        if (this.realSubmitButton.hasAttribute('disabled')) {
          this.scrollToVariantPicker();
          return;
        }
        if (this.realSubmitButton.getAttribute('aria-disabled') === 'true') return;
        this.realSubmitButton.click();
      }

      scrollToVariantPicker() {
        const target = this.variantSelects || this.realSubmitButton.closest('product-form');
        if (!target) return;

        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.classList.add('sticky-add-to-cart__highlight');
        window.setTimeout(() => target.classList.remove('sticky-add-to-cart__highlight'), 1200);
      }
    }
  );
}
